import {Rail, Segment, Track} from "./SegmentedTabs.styles";

export interface SegmentOption<T extends string> {
	/** The value reported through {@link SegmentedTabsProps.onChange} when this segment is picked. */
	value: T;
	/** The visible, already-localized segment label. */
	label: string;
}

export interface SegmentedTabsProps<T extends string> {
	/** The ordered segments to render (typically two). */
	options: readonly SegmentOption<T>[];
	/** The currently-active value. */
	value: T;
	/** Fired with the picked value when a segment is activated. */
	onChange: (value: T) => void;
	/** Accessible name for the `tablist` (e.g. `common.pageSections`). */
	ariaLabel: string;
}

/**
 * The house segmented tab pill (ui#163) — the pricing {@link ../pricing/BillingToggle} `.billtog`
 * idiom generalized to N options. Replaces MUI Tabs on the feature panels (Contacts/Groups,
 * Active/Inactive) so those bodies share the v3 language. A dumb view (epic #26): the active value
 * plus its setter are the whole contract; colours come from `theme.tokens`. `role="tablist"` +
 * `role="tab"` + `aria-selected` name the control for assistive tech, mirroring the billing toggle.
 */
export function SegmentedTabs<T extends string>({options, value, onChange, ariaLabel}: SegmentedTabsProps<T>) {
	return (
		<Rail>
			<Track role="tablist" aria-label={ariaLabel}>
				{options.map(option => (
					<Segment
						key={option.value}
						type="button"
						role="tab"
						aria-selected={option.value === value}
						$active={option.value === value}
						onClick={() => onChange(option.value)}
					>
						{option.label}
					</Segment>
				))}
			</Track>
		</Rail>
	);
}
